import { Link } from "@inertiajs/react"
import { AlertCircle, AlertTriangle, Info } from "lucide-react"

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"

interface Warning {
  severity: "error" | "warning" | "info"
  title: string
  message: string
  action_text?: string
  action_path?: string
}

interface ConfigWarningsProps {
  warnings: Warning[]
  className?: string
}

const severityConfig = {
  error: {
    icon: AlertCircle,
    variant: "destructive" as const,
    iconColor: "text-destructive",
  },
  warning: {
    icon: AlertTriangle,
    variant: "default" as const,
    iconColor: "text-yellow-600 dark:text-yellow-500",
  },
  info: {
    icon: Info,
    variant: "default" as const,
    iconColor: "text-blue-600 dark:text-blue-500",
  },
}

export function ConfigWarnings({ warnings, className }: ConfigWarningsProps) {
  if (warnings.length === 0) return null

  return (
    <div className={cn("space-y-3", className)}>
      {warnings.map((warning, index) => {
        const config = severityConfig[warning.severity]
        const Icon = config.icon

        return (
          <Alert key={index} variant={config.variant}>
            <Icon className={config.iconColor} />
            <AlertTitle>{warning.title}</AlertTitle>
            <AlertDescription>
              <div className="flex items-center justify-between gap-4">
                <p className="flex-1">{warning.message}</p>
                {warning.action_text && warning.action_path && (
                  <Button variant="outline" size="sm" asChild>
                    <Link href={warning.action_path}>
                      {warning.action_text}
                    </Link>
                  </Button>
                )}
              </div>
            </AlertDescription>
          </Alert>
        )
      })}
    </div>
  )
}
